import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { cn } from '@/lib/utils';
import { DEV_KIT_WRENCH, DevKitChip } from './DevKitInline';

/**
 * One global, sticky collapse preference shared by every dev kit surface:
 * collapse the kit on any page and it stays a pill everywhere.
 * `app/views/shared/_dev_kit_panel.html.erb` repeats the literal because its
 * collapse script is inlined outside the Vite pipeline (ActiveAdmin renders it
 * too); the two must stay in step.
 */
export const DEV_KIT_COLLAPSED_KEY = 'ably_dev_kit_collapsed';

// localStorage can be unavailable (private mode, blocked cookies) and absent
// under SSR; the panel still works, it just will not remember the preference.
const readCollapsed = (): boolean => {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(DEV_KIT_COLLAPSED_KEY) === '1';
  } catch {
    return false;
  }
};

const writeCollapsed = (collapsed: boolean): void => {
  try {
    window.localStorage.setItem(DEV_KIT_COLLAPSED_KEY, collapsed ? '1' : '0');
  } catch {
    return;
  }
};

/** Body control (`<select>`, `<input>`), mirroring `.dev-kit-select` / `.dev-kit-input`. */
export const devKitControlClass =
  'focus-base block w-full rounded-md border border-neutral-500 bg-neutral-100 px-2.5 py-1.5 text-xs text-neutral-1300 placeholder:text-neutral-700';

/** Footnote beneath a panel's controls, mirroring `.dev-kit-foot`. */
export function DevKitFoot({ className, ...rest }: React.HTMLAttributes<HTMLParagraphElement>) {
  return (
    <p
      className={cn(
        'text-xs text-neutral-900',
        '[&_code]:rounded-[3px] [&_code]:bg-neutral-200 [&_code]:px-1 [&_code]:py-px [&_code]:font-mono [&_code]:text-neutral-1100',
        className,
      )}
      {...rest}
    />
  );
}

interface DevKitPanelProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Sentence-case heading, e.g. "One-click login". */
  title: string;
  /** Short label the collapsed pill carries, e.g. "Sign in". */
  action: string;
  children: React.ReactNode;
}

/**
 * Floating variant of the dev kit: the React counterpart of
 * `app/views/shared/_dev_kit_panel.html.erb`, sharing its chip with
 * `DevKitInline`. Render it once per page at page root - it is fixed
 * bottom-left (bottom-right is the support chat widget), so it never affects
 * the host page's layout, and its controls sit outside any product form.
 *
 * Both states stay mounted and are toggled with `hidden` so `aria-controls`
 * resolves either way and a part-filled control survives a collapse.
 */
export function DevKitPanel({ title, action, children, className, ...rest }: DevKitPanelProps) {
  const panelId = useId();
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const collapseRef = useRef<HTMLButtonElement>(null);
  const pillRef = useRef<HTMLButtonElement>(null);
  const toggled = useRef(false);

  const collapse = useCallback(() => {
    toggled.current = true;
    setCollapsed(true);
    writeCollapsed(true);
  }, []);

  const expand = useCallback(() => {
    toggled.current = true;
    setCollapsed(false);
    writeCollapsed(false);
  }, []);

  // Each toggle hides the control that was clicked, so focus has to follow it
  // to the control that took its place or it falls back to the body. Skipped on
  // mount, where nothing was clicked and the page owns focus.
  useEffect(() => {
    if (!toggled.current) return;
    (collapsed ? pillRef : collapseRef).current?.focus();
  }, [collapsed]);

  return (
    <div
      className={cn('fixed bottom-4 left-4 z-50 text-left font-sans leading-[1.4] text-neutral-1300', className)}
      {...rest}
    >
      <div
        id={panelId}
        hidden={collapsed}
        className={cn(
          'w-72 max-w-[calc(100vw-2rem)] rounded-[10px] border-[1.5px] border-dashed border-neutral-900',
          'bg-neutral-000 p-3.5 shadow-lg',
          collapsed && 'hidden',
        )}
      >
        <div className="mb-2.5 flex items-center justify-between gap-2">
          <span className="flex min-w-0 items-center gap-1.5">
            <span aria-hidden="true" className="text-xs leading-none">
              {DEV_KIT_WRENCH}
            </span>
            <DevKitChip />
            <span className="truncate text-xs font-semibold text-neutral-1100">{title}</span>
          </span>
          <button
            ref={collapseRef}
            type="button"
            onClick={collapse}
            aria-label="Collapse dev tools"
            aria-controls={panelId}
            aria-expanded="true"
            className="focus-base rounded px-0.5 text-xs leading-none text-neutral-700 transition-colors hover:text-orange-600"
          >
            &times;
          </button>
        </div>

        <div className="flex flex-col gap-2">{children}</div>
      </div>

      <button
        ref={pillRef}
        type="button"
        hidden={!collapsed}
        onClick={expand}
        aria-controls={panelId}
        aria-expanded="false"
        className={cn(
          'focus-base inline-flex items-center gap-1.5 rounded-full border-[1.5px] border-dashed border-neutral-700',
          'bg-neutral-1300 py-1.5 pl-3 pr-3 text-neutral-000 shadow-lg',
          'transition-colors hover:border-orange-500',
          !collapsed && 'hidden',
        )}
      >
        <span aria-hidden="true" className="text-xs leading-none">
          {DEV_KIT_WRENCH}
        </span>
        <span className="font-mono text-xs font-semibold tracking-[0.12em]">DEV</span>
        {/* Whitespace-only, so flex layout ignores it but the accessible name
            reads "DEV Sign in" rather than running the two labels together. */}{' '}
        <span className="text-xs text-neutral-500">{action}</span>
      </button>
    </div>
  );
}

export default DevKitPanel;
